import { useFragment, useLazyLoadQuery } from "react-relay";
import { graphql } from "relay-runtime";
import { ProductCard } from "./ProductCard";
import { ProductTrackFragment$key } from "./__generated__/ProductTrackFragment.graphql";
import { Component, ReactNode, Suspense, useEffect, useMemo } from "react";
import { useI18nContext } from "@i18n/i18n-react";
import { getCategoryName } from "@utils/language";
import { ProductTrackInstantBuysQuery } from "./__generated__/ProductTrackInstantBuysQuery.graphql";
import { useUserInfo } from "@features/auth/useAuth";
import { isDiscountName } from "@utils/discount";
import { isAfter } from "date-fns";
import { cn } from "@shadcn/lib/utils";

const fragment = graphql`
	fragment ProductTrackFragment on HubProductPage {
		nodes {
			id
			stock
			priceAmount
			outOfStock
			product {
				id
				name
				brand {
					id
					organization {
						id
						name
					}
				}
				category {
					id
					name
				}
			}
			...ProductCardFragment
		}
	}
`;

const instantBuysQuery = graphql`
	query ProductTrackInstantBuysQuery($userId: UUID!) {
		user(id: $userId) {
			instantBuys(pagination: { page: 0, pageSize: 1000000 }, orderBy: [{ field: CREATED_AT, ordering: ASC }]) {
				nodes {
					id
					status
					purchasedAt
					lineItems {
						priceAmount
						productInstance {
							hubProduct {
								id
								product {
									id
									name
								}
							}
						}
					}
				}
			}
		}
	}
`;

interface Category {
	id: string;
	name: string;
	i18N: { DE: string };
}

interface ProductTrackProps {
	products: ProductTrackFragment$key;
	tab?: string;
	categories?: Array<Category | null | undefined>;
	type?: "brand" | "category" | "none";
	searchValue?: string;
	showAddButton?: boolean;
	hideOutOfStock?: boolean;
	onProductClick?: (hubProductId: string) => void;
	title?: string;
	setHasDiscount?: (has: boolean) => void;
	className?: string;
}

type BoughtHubProduct = NonNullable<
	ProductTrackInstantBuysQuery["response"]["user"]
>["instantBuys"]["nodes"][number]["lineItems"][number]["productInstance"]["hubProduct"];

const discountStartDate = new Date("2026-03-04T02:00:00+00:00");
const checkDate = (withPurchasedAt: { purchasedAt: string }) => isAfter(new Date(withPurchasedAt.purchasedAt), discountStartDate);

const getBoughtHubProducts = (instantBuyData: ProductTrackInstantBuysQuery["response"]) => {
	const nodes = instantBuyData.user?.instantBuys.nodes ?? [];
	return [
		...new Map(
			nodes
				.filter(checkDate)
				.flatMap((b) => b.lineItems)
				.map((li) => li.productInstance.hubProduct)
				.map((hp) => [hp.id, hp] as const)
		).values(),
	];
};

class ProductTrackInstantBuysErrorBoundary extends Component<
	{
		children: ReactNode;
		fallback: ReactNode;
	},
	{
		hasError: boolean;
	}
> {
	state = {
		hasError: false,
	};

	static getDerivedStateFromError() {
		return { hasError: true };
	}

	render() {
		if (this.state.hasError) {
			return this.props.fallback;
		}

		return this.props.children;
	}
}

const ProductTrackBody = ({ props, boughtHubProducts }: { props: ProductTrackProps; boughtHubProducts: BoughtHubProduct[] }) => {
	const { LL, locale } = useI18nContext();
	const i18n = LL["new-ui"];
	const {
		showAddButton = true,
		hideOutOfStock = false,
		onProductClick,
		categories,
		tab,
		type,
		searchValue,
		title = i18n.RECOMMENDED_TITLE(),
	} = props;
	const { nodes: products } = useFragment(fragment, props.products);

	const sortedProducts = useMemo(
		() =>
			products.filter(hideOutOfStock ? (p) => p.stock > 0 : () => true).sort((a, b) => {
				const aOutOfStock = a.stock === 0;
				const bOutOfStock = b.stock === 0;

				if (aOutOfStock && !bOutOfStock) return 1;
				if (!aOutOfStock && bOutOfStock) return -1;

				return b.stock - a.stock;
			}),
		[products]
	);

	const filteredProducts = useMemo(() => {
		let nodes = sortedProducts;
		if (type === "brand") {
			nodes = sortedProducts.filter((p) => p.product.brand.organization.name === tab);
		} else if (type === "category") {
			nodes = sortedProducts.filter((p) => p.product.category?.name === tab);
		}

		if (searchValue) {
			const search = searchValue.toLowerCase();
			nodes = nodes.filter(
				(p) => p.product.name.toLowerCase().includes(search) || p.product.brand.organization.name.toLowerCase().includes(search)
			);
		}

		const discountedNodes = nodes.filter((p) => isDiscountName(p.product.name));
		const nonDiscountedNodes = tab === "discounted" && !type ? [] : nodes.filter((p) => !isDiscountName(p.product.name));

		return {
			discountedNodes,
			nonDiscountedNodes,
		};
	}, [tab, type, searchValue, sortedProducts]);

	const recommendedProducts = useMemo(
		() => filteredProducts.nonDiscountedNodes.slice(0, 4),
		[filteredProducts.nonDiscountedNodes]
	);

	const uncategorizedProducts = useMemo(() => {
		const recommendedIds = new Set(recommendedProducts.map((product) => product.id));

		return filteredProducts.nonDiscountedNodes.filter(
			(product) => !product.product.category && !recommendedIds.has(product.id)
		);
	}, [filteredProducts.nonDiscountedNodes, recommendedProducts]);

	const mappedProducts = useMemo(() => {
		let map: Map<
			string,
			{
				category: Category;
				products: (typeof filteredProducts)["nonDiscountedNodes"];
			}
		> = new Map();
		for (const category of categories ?? []) {
			if (!category) {
				continue;
			}

			const currentProducts = filteredProducts.nonDiscountedNodes.filter((p) => p.product.category?.name === category.name);
			if (currentProducts.length) {
				map.set(category.id, {
					category: category,
					products: currentProducts,
				});
			}
		}

		return map;
	}, [filteredProducts, categories]);

	const hasDiscount = useMemo(() => boughtHubProducts.length > 0, [boughtHubProducts]);

	useEffect(() => {
		if (typeof props.setHasDiscount === "function") {
			props.setHasDiscount(hasDiscount);
		}
	}, [hasDiscount, props.setHasDiscount]);

	return (
		<div className={cn("px-6 py-4", props.className)}>
			{hasDiscount && (!type || type === "none" || (!type && tab === "discounted")) ? (
				<div className="mx-auto max-w-md mb-6">
					<h2 className="text-2xl font-breit text-neutral-900">{i18n.DISCOUNTED()}</h2>
					<div className="mt-4 grid grid-cols-2 gap-4">
						{filteredProducts.discountedNodes.map((p) => (
							<ProductCard key={p.id} product={p} showAddButton={showAddButton} onProductClick={onProductClick} />
						))}
					</div>
				</div>
			) : null}

			{(!type || type === "none") && filteredProducts.nonDiscountedNodes.length ? (
				<div className="mx-auto max-w-md mb-6">
					<h2 className="text-2xl font-breit text-neutral-900">{title}</h2>
					<div className="mt-4 grid grid-cols-2 gap-4">
						{recommendedProducts.map((p) => (
							<ProductCard key={p.id} product={p} showAddButton={showAddButton} onProductClick={onProductClick} />
						))}
					</div>
				</div>
			) : null}

			{Array.from(mappedProducts.keys()).map((key: string) => {
				const section = mappedProducts.get(key);
				if (!section) {
					return null;
				}

				return (
					<div className="mx-auto max-w-md mb-6" key={key}>
						<h2 className="text-2xl font-breit text-neutral-900">{getCategoryName(section.category, locale)}</h2>
						<div className="mt-4 grid grid-cols-2 gap-4">
							{section.products.map((p) => (
								<ProductCard key={p.id} product={p} showAddButton={showAddButton} onProductClick={onProductClick} />
							))}
						</div>
					</div>
				);
			})}

			{(!type || type === "none") && uncategorizedProducts.length ? (
				<div className="mx-auto max-w-md mb-6">
					<h2 className="text-2xl font-breit text-neutral-900">{i18n.MORE_PRODUCTS()}</h2>
					<div className="mt-4 grid grid-cols-2 gap-4">
						{uncategorizedProducts.map((p) => (
							<ProductCard key={p.id} product={p} showAddButton={showAddButton} onProductClick={onProductClick} />
						))}
					</div>
				</div>
			) : null}
		</div>
	);
};

const ProductTrackWithInstantBuys = ({ props, userId }: { props: ProductTrackProps; userId: string }) => {
	const instantBuyData = useLazyLoadQuery<ProductTrackInstantBuysQuery>(
		instantBuysQuery,
		{
			userId,
		},
		{ fetchPolicy: "store-and-network" }
	);

	return <ProductTrackBody props={props} boughtHubProducts={getBoughtHubProducts(instantBuyData)} />;
};

export const ProductTrack = (props: ProductTrackProps) => {
	const user = useUserInfo();
	const fallback = <ProductTrackBody props={props} boughtHubProducts={[]} />;

	if (!user?.id) {
		return fallback;
	}

	return (
		<ProductTrackInstantBuysErrorBoundary fallback={fallback}>
			<Suspense fallback={fallback}>
				<ProductTrackWithInstantBuys props={props} userId={user.id} />
			</Suspense>
		</ProductTrackInstantBuysErrorBoundary>
	);
};
